// Query options for Operator › Deploys (DC-04): the deploy list, one deploy with its bundle, and
// the DEs a deploy can target. Keys nest under `["operator", "deploys", …]`, so one invalidation
// reaches the screen. While a deploy is on its way (approved, handed off, or a redeploy waiting
// for the worker) the queries poll, so the timeline moves without a reload.
import { queryOptions } from "@tanstack/react-query";
import type { Api } from "../../../api/client";
import type { Deploy } from "../../../api/types";

export const deployKeys = {
  all: () => ["operator", "deploys"] as const,
  list: () => ["operator", "deploys", "list"] as const,
  detail: (id: string) => ["operator", "deploys", "detail", id] as const,
  targets: () => ["operator", "deploys", "targets"] as const,
};

/** Polling interval while a deploy is moving (the hand-off worker runs every 15 s). */
export const MOVING_POLL_MS = 2000;

/** Whether the platform is still working on the deploy (no person action pending). */
export function isMoving(d: Pick<Deploy, "state" | "path" | "kind">): boolean {
  if (d.state === "approved" || d.state === "handed_off") return true;
  return d.state === "staged" && !(d.path === "done_for_you" && d.kind === "first");
}

export const deploysQuery = (api: Api) =>
  queryOptions({
    queryKey: deployKeys.list(),
    queryFn: () => api.client.operator.deploys.list({ query: { limit: 200 } }),
    refetchInterval: (q) => (q.state.data?.data.some(isMoving) ? MOVING_POLL_MS : false),
  });

export const deployQuery = (api: Api, deployId: string) =>
  queryOptions({
    queryKey: deployKeys.detail(deployId),
    queryFn: () => api.client.operator.deploys.get({ params: { deploy_id: deployId } }),
    refetchInterval: (q) =>
      q.state.data && isMoving(q.state.data.deploy) ? MOVING_POLL_MS : false,
  });

/** Every DE across the caller's operator companies (the console's DE list, this week). */
export const deployTargetsQuery = (api: Api) =>
  queryOptions({
    queryKey: deployKeys.targets(),
    queryFn: () => api.client.operator.digitalEmployees.list({ query: {} }),
  });
